import { useCallback } from 'react'
import { z } from 'zod'
import { create } from 'zustand'
import { DetectionResult, GetDetectionsRequest, GetDetectionsRequestSchema } from './get_detections'
import { ClientFilters, INITIAL_CLIENT_FILTERS } from './detectionFilters'
import { ImageRequest } from './list_image_requests'
import { clampToWindow, computeWindowBounds } from './timeWindow'
import {
    CardSize,
    CardSizeEnum,
    DETECTION_EXPLORER_FILTER_DEFINITIONS,
    DETECTION_EXPLORER_TIME_PRESETS,
    DetectionExplorerFilterKey,
    TimePreset
} from './config'

export enum ViewMode {
    ListView = 'listView',
    GalleryView = 'galleryView',
    GroupByLPView = 'groupByLPView'
}

// Display preferences persisted across sessions. localStorage is untrusted input, so reads go
// through a Zod boundary (rules/main.md), mirroring useSidebarStore.
const VIEW_MODE_STORAGE_KEY = 'detectionExplorer:viewMode'
const CARD_SIZE_STORAGE_KEY = 'detectionExplorer:cardSize'
const viewModeSchema = z.nativeEnum(ViewMode)

const readPersisted = <T,>(key: string, schema: z.ZodType<T>, fallback: T): T => {
    try {
        const raw = localStorage.getItem(key)
        if (raw == null) return fallback
        return schema.parse(JSON.parse(raw))
    } catch {
        return fallback
    }
}

const writePersisted = (key: string, value: unknown): void => {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Ignore write failures (e.g. private mode / storage disabled).
    }
}

const initialViewMode = readPersisted(VIEW_MODE_STORAGE_KEY, viewModeSchema, ViewMode.GroupByLPView)
const initialCardSize = readPersisted<CardSize>(CARD_SIZE_STORAGE_KEY, CardSizeEnum, 'medium')

type BaseFilterKey = 'lot' | 'from' | 'to' | 'device_uuids'
type ChangeableFilterKey = Exclude<keyof GetDetectionsRequest, 'lot'>

const BaseFiltersSchema = GetDetectionsRequestSchema.pick({
    lot: true,
    from: true,
    to: true,
    device_uuids: true
}).partial()
type BaseFilters = Partial<Pick<GetDetectionsRequest, BaseFilterKey>>

// One row in the filter chip strip. `value` shape depends on the filter's input type:
//   singleText  → string
//   multiSelect → string[]
//   numberRange → { min?: number; max?: number } (values in 0–1 range, scaled for display)
export type FilterChipValue = string | string[] | { min?: number; max?: number }

export type FilterInstance = {
    id: string
    key: DetectionExplorerFilterKey
    value: FilterChipValue
}

const emptyValueFor = (key: DetectionExplorerFilterKey): FilterChipValue => {
    const def = DETECTION_EXPLORER_FILTER_DEFINITIONS[key]
    switch (def.input.type) {
        case 'singleText':
            return ''
        case 'multiSelect':
            return []
        case 'numberRange':
            return {}
    }
}

const INITIAL_FILTERS: GetDetectionsRequest = {
    lot: '',
    from: new Date(0),
    to: new Date(0),
    device_uuids: [],
    page: 1,
    // Keep grouping in sync with the (persisted) initial view mode so the first request matches.
    group_by_license_plate: initialViewMode === ViewMode.GroupByLPView
}

let nextInstanceId = 1
const createInstanceId = () => `f${nextInstanceId++}`

type DetectionExplorerStore = {
    viewMode: ViewMode
    setViewMode: (mode: ViewMode) => void

    // Whether the Image Requests side panel is open. Toggled from the Filters toolbar.
    imageRequestsOpen: boolean
    toggleImageRequests: () => void

    filters: GetDetectionsRequest
    setBaseFilters: (base: BaseFilters) => void
    changeFilter: <K extends ChangeableFilterKey>(key: K, value: GetDetectionsRequest[K]) => void
    resetForLot: (base: { lot: string; from: Date; to: Date }) => void

    // Selected time-range preset. The hour presets snap from/to to [now − N, now]; 'custom' keeps the
    // current window and reveals the start/end pickers. Editing those pickers switches it to 'custom'.
    timePreset: TimePreset
    setTimePreset: (preset: TimePreset) => void

    // Card-size scale for the gallery / group-by-LP grids. A display preference, kept across lots.
    cardSize: CardSize
    setCardSize: (size: CardSize) => void

    // Client-side result filters (image presence / LP-case content). Not server-filterable, so they
    // drive an accumulating fetch (see useFilteredDetections). Toggling one resets to page 1.
    clientFilters: ClientFilters
    setClientFilter: <K extends keyof ClientFilters>(key: K, value: boolean) => void

    // The image request currently driving the view (highlighted in the panel + shown as a
    // dismissible indicator), or null. `applyImageRequest` maps a request's `detection_selector`
    // onto the filters: a `timerange` sets the window, `detection_uuids` narrows to those rows
    // (over the max window, since get_detections still needs a valid window + device_uuids).
    appliedImageRequestId: string | null
    applyImageRequest: (request: ImageRequest) => void
    clearAppliedImageRequest: () => void

    activeFilters: FilterInstance[]
    addActiveFilter: (key: DetectionExplorerFilterKey) => void
    updateActiveFilter: (id: string, value: FilterChipValue) => void
    removeActiveFilter: (id: string) => void
    clearActiveFilters: () => void
    // Adds a numeric device_id to the (single) device filter chip, creating it if needed. Used by
    // the clickable device badge in the table.
    addDeviceToFilter: (deviceId: number) => void

    // Full selected rows keyed by detection_uuid, so a selection keeps its data across
    // pages (only the current page is ever in memory) and the export can use it directly.
    selectedById: Record<string, DetectionResult>
    toggleSelected: (detection: DetectionResult) => void
    setPageSelection: (pageItems: DetectionResult[], selected: boolean) => void
    clearSelection: () => void
}

export const useDetectionExplorerStore = create<DetectionExplorerStore>((set) => ({
    viewMode: initialViewMode,
    setViewMode: (mode) =>
        set((state) => {
            writePersisted(VIEW_MODE_STORAGE_KEY, mode)
            const shouldResetPagination =
                (state.viewMode === ViewMode.GroupByLPView) !== (mode === ViewMode.GroupByLPView)
            return {
                viewMode: mode,
                filters: {
                    ...state.filters,
                    group_by_license_plate: mode === ViewMode.GroupByLPView,
                    ...(shouldResetPagination ? { page: 1 } : {})
                }
            }
        }),

    imageRequestsOpen: false,
    toggleImageRequests: () => set((state) => ({ imageRequestsOpen: !state.imageRequestsOpen })),

    filters: INITIAL_FILTERS,
    setBaseFilters: (base) => {
        const result = BaseFiltersSchema.safeParse(base)
        if (!result.success) {
            // eslint-disable-next-line no-console
            console.warn('[DetectionExplorerStore] invalid base filters:', result.error.issues)
            return
        }
        set((state) => ({ filters: { ...state.filters, ...result.data } }))
    },
    changeFilter: (key, value) => {
        const fieldSchema = GetDetectionsRequestSchema.shape[key]
        const result = fieldSchema.safeParse(value)
        if (!result.success) {
            // eslint-disable-next-line no-console
            console.warn(`[DetectionExplorerStore] invalid value for ${key}:`, result.error.issues)
            return
        }
        set((state) => ({ filters: { ...state.filters, [key]: result.data } }))
    },
    resetForLot: ({ lot, from, to }) =>
        set((state) => ({
            // A lot reset restores the full 48h window, so the preset returns to its default too.
            filters: { ...state.filters, lot, from, to, page: 1, detection_uuids: undefined },
            activeFilters: [],
            selectedById: {},
            appliedImageRequestId: null,
            clientFilters: INITIAL_CLIENT_FILTERS,
            timePreset: '48h'
        })),

    timePreset: '48h',
    setTimePreset: (preset) =>
        set((state) => {
            // 'custom' just reveals the pickers; it leaves the current window untouched.
            if (preset === 'custom') return { timePreset: 'custom' }
            const minutes = DETECTION_EXPLORER_TIME_PRESETS.find((p) => p.key === preset)?.minutes
            if (minutes == null) return {}
            const bounds = computeWindowBounds()
            const from = clampToWindow(new Date(bounds.now.getTime() - minutes * 60 * 1000), bounds)
            return { timePreset: preset, filters: { ...state.filters, from, to: bounds.now, page: 1 } }
        }),

    cardSize: initialCardSize,
    setCardSize: (size) => {
        writePersisted(CARD_SIZE_STORAGE_KEY, size)
        set({ cardSize: size })
    },

    clientFilters: INITIAL_CLIENT_FILTERS,
    setClientFilter: (key, value) =>
        set((state) => ({
            clientFilters: { ...state.clientFilters, [key]: value },
            // The display-page index is shared with the unfiltered path, so a toggle restarts at 1.
            filters: { ...state.filters, page: 1 }
        })),

    appliedImageRequestId: null,
    applyImageRequest: (request) =>
        set((state) => {
            const selector = request.detection_selector
            const bounds = computeWindowBounds()
            const window =
                selector.type === 'detection_uuids'
                    ? { from: bounds.cutoff, to: bounds.now }
                    : {
                          from: clampToWindow(new Date(selector.lower), bounds),
                          to: clampToWindow(new Date(selector.upper), bounds)
                      }
            return {
                filters: {
                    ...state.filters,
                    ...window,
                    page: 1,
                    detection_uuids: selector.type === 'detection_uuids' ? selector.detection_uuids : undefined
                },
                // Applying replaces the current scope: drop chips and selection so the view shows
                // exactly what the request targeted.
                activeFilters: [],
                selectedById: {},
                appliedImageRequestId: request.request_uuid
            }
        }),
    clearAppliedImageRequest: () =>
        set((state) => ({
            filters: { ...state.filters, detection_uuids: undefined, page: 1 },
            appliedImageRequestId: null
        })),

    activeFilters: [],
    addActiveFilter: (key) =>
        set((state) => ({
            activeFilters: [...state.activeFilters, { id: createInstanceId(), key, value: emptyValueFor(key) }],
            filters: { ...state.filters, page: 1 }
        })),
    updateActiveFilter: (id, value) =>
        set((state) => ({
            activeFilters: state.activeFilters.map((f) => (f.id === id ? { ...f, value } : f)),
            filters: { ...state.filters, page: 1 }
        })),
    removeActiveFilter: (id) =>
        set((state) => ({
            activeFilters: state.activeFilters.filter((f) => f.id !== id),
            filters: { ...state.filters, page: 1 }
        })),
    clearActiveFilters: () =>
        set((state) => ({
            activeFilters: [],
            filters: { ...state.filters, page: 1 }
        })),
    addDeviceToFilter: (deviceId) =>
        set((state) => {
            const existing = state.activeFilters.find((f) => f.key === 'device')
            const current = existing && Array.isArray(existing.value) ? existing.value : []
            const id = String(deviceId)
            const nextIds = current.includes(id) ? current : [...current, id]
            const activeFilters = existing
                ? state.activeFilters.map((f) => (f.id === existing.id ? { ...f, value: nextIds } : f))
                : [...state.activeFilters, { id: createInstanceId(), key: 'device' as const, value: nextIds }]
            return {
                activeFilters,
                filters: { ...state.filters, page: 1 }
            }
        }),

    selectedById: {},
    toggleSelected: (detection) =>
        set((state) => {
            const next = { ...state.selectedById }
            if (next[detection.detection_uuid]) {
                delete next[detection.detection_uuid]
            } else {
                next[detection.detection_uuid] = detection
            }
            return { selectedById: next }
        }),
    setPageSelection: (pageItems, selected) =>
        set((state) => {
            const next = { ...state.selectedById }
            for (const item of pageItems) {
                if (selected) {
                    next[item.detection_uuid] = item
                } else {
                    delete next[item.detection_uuid]
                }
            }
            return { selectedById: next }
        }),
    clearSelection: () => set({ selectedById: {} })
}))

/**
 * Toggle selection from a UI that only carries the detection id (e.g. a gallery card),
 * resolving the full row from the rows currently in view. Selections made on other pages
 * stay in the store with their data intact, so the export covers them too.
 */
export const useToggleSelectedDetection = (items: DetectionResult[]) => {
    const toggleSelected = useDetectionExplorerStore((s) => s.toggleSelected)
    return useCallback(
        (id: string) => {
            const detection = items.find((d) => d.detection_uuid === id)
            if (detection) toggleSelected(detection)
        },
        [items, toggleSelected]
    )
}

// ============================================
// Derive request-shape filter fields from active chip instances.
// ============================================

const dedupe = (arr: string[]) => Array.from(new Set(arr))

const cleanArray = (arr: string[]): string[] | undefined => {
    const cleaned = dedupe(arr.map((s) => s.trim()).filter(Boolean))
    return cleaned.length > 0 ? cleaned : undefined
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Derives the schema fields (license_plate[], category[], vehicle_type[], direction[],
 * lpr_conf_min/max, lpness_min/max) from `activeFilters`.
 * - `singleText` chips with the same key are aggregated into one array.
 * - `multiSelect` chips own the whole array.
 * - `numberRange` chips set min/max.
 */
export const buildRequestFromChips = (base: GetDetectionsRequest, chips: FilterInstance[]): GetDetectionsRequest => {
    const arrayBuckets: Record<string, string[]> = {}
    const overrides: Partial<GetDetectionsRequest> = {
        license_plate: undefined,
        category: undefined,
        vehicle_type: undefined,
        direction: undefined,
        country: undefined,
        lpr_conf_min: undefined,
        lpr_conf_max: undefined,
        lpness_min: undefined,
        lpness_max: undefined
    }

    for (const chip of chips) {
        const def = DETECTION_EXPLORER_FILTER_DEFINITIONS[chip.key]
        if (def.input.type === 'singleText' && typeof chip.value === 'string') {
            const key = def.input.requestKey
            const bucket = arrayBuckets[key] ?? []
            bucket.push(chip.value)
            arrayBuckets[key] = bucket
        } else if (def.input.type === 'multiSelect' && Array.isArray(chip.value)) {
            arrayBuckets[def.input.requestKey] = [...(arrayBuckets[def.input.requestKey] ?? []), ...chip.value]
        } else if (def.input.type === 'numberRange' && !Array.isArray(chip.value) && typeof chip.value === 'object') {
            overrides[def.input.minRequestKey] = chip.value.min
            overrides[def.input.maxRequestKey] = chip.value.max
        }
    }

    for (const [requestKey, values] of Object.entries(arrayBuckets)) {
        // Detection ids must be full UUIDs or the backend 400s the whole query. Drop partial/invalid
        // entries (e.g. while the user is still typing one) so a half-typed id never fires a doomed
        // request — the filter simply doesn't apply until a valid UUID is entered.
        const source = requestKey === 'detection_uuids' ? values.filter((v) => UUID_RE.test(v.trim())) : values
        const cleaned = cleanArray(source)
        // `detection_uuids` (e.g. from an applied image request) lives on the base request. An empty
        // chip must leave that base value untouched rather than overriding it to undefined.
        if (requestKey === 'detection_uuids' && cleaned === undefined) continue
        // The device chip carries stringified numeric device_ids; the request wants real numbers.
        if (requestKey === 'device_ids') {
            overrides.device_ids = cleaned?.map(Number)
            continue
        }
        overrides[requestKey as keyof GetDetectionsRequest] = cleaned as never
    }

    return { ...base, ...overrides }
}

/**
 * True when a Detection ID chip holds a non-empty value that isn't a complete UUID. The request
 * drops such values (so it never 400s); the view uses this to show an empty state instead of
 * silently displaying unfiltered results while the id is still being typed.
 */
export const hasIncompleteDetectionId = (chips: FilterInstance[]): boolean =>
    chips.some(
        (chip) =>
            chip.key === 'detection_uuid' &&
            typeof chip.value === 'string' &&
            chip.value.trim() !== '' &&
            !UUID_RE.test(chip.value.trim())
    )
